import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TitleIcon from '@mui/icons-material/Title';
import NotesIcon from '@mui/icons-material/Notes';
import ImageIcon from '@mui/icons-material/Image';
import SmartButtonIcon from '@mui/icons-material/SmartButton';
import MovieIcon from '@mui/icons-material/Movie';
import GridViewIcon from '@mui/icons-material/GridView';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import HeightIcon from '@mui/icons-material/Height';

/**
 * Asks what is being added, instead of guessing.
 *
 * "Add" used to drop in a paragraph every time, so building a row of pricing
 * cards meant adding three paragraphs and then changing each one's type from
 * a dropdown — and the way to get a picture was to know that trick. The list
 * of what this page can hold was real and complete and nowhere on screen.
 *
 * Grouped by what the thing is for rather than alphabetically: someone
 * arriving here wants "a picture" or "a row of three", and those are further
 * apart in the alphabet than they are in intent.
 */

const GROUPS = [
  {
    label: 'Words',
    items: [
      { type: 'heading', icon: TitleIcon, label: 'Heading', hint: 'A title or a line that stands out' },
      { type: 'text', icon: NotesIcon, label: 'Text', hint: 'A paragraph' },
    ],
  },
  {
    label: 'Media',
    items: [
      { type: 'image', icon: ImageIcon, label: 'Picture', hint: 'Upload one — it is kept with your brand' },
      { type: 'video', icon: MovieIcon, label: 'Video', hint: 'A YouTube link or a file' },
    ],
  },
  {
    label: 'Arrangement',
    items: [
      {
        type: 'grid',
        icon: GridViewIcon,
        label: 'Grid',
        hint: 'A row of things side by side — put cards or pictures inside it',
        container: true,
      },
      {
        type: 'card',
        icon: CreditCardIcon,
        label: 'Card',
        hint: 'A box that holds a heading, text and a button together',
        container: true,
      },
    ],
  },
  {
    label: 'The rest',
    items: [
      { type: 'button', icon: SmartButtonIcon, label: 'Button', hint: 'Sends people somewhere' },
      { type: 'divider', icon: HorizontalRuleIcon, label: 'Line', hint: 'A rule between things' },
      { type: 'spacer', icon: HeightIcon, label: 'Gap', hint: 'Empty space' },
    ],
  },
];

export default function AddBlockDialog({ open, onClose, onPick, insideLabel }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ pr: '48px' }}>
        <Typography component='span' fontWeight='700'>
          {insideLabel ? `Add inside ${insideLabel}` : 'Add to this section'}
        </Typography>
        <IconButton
          size='small'
          onClick={onClose}
          aria-label='Close'
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing='18px'>
          {GROUPS.map((g) => (
            <Box key={g.label}>
              <Typography
                variant='caption'
                fontWeight='700'
                color='text.secondary'
                textTransform='uppercase'
                display='block'
                mb='8px'
              >
                {g.label}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: '8px',
                }}
              >
                {g.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <Box
                      key={it.type}
                      component='button'
                      type='button'
                      onClick={() => { onPick(it.type); onClose(); }}
                      sx={{
                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                        textAlign: 'left', width: '100%',
                        p: '12px', borderRadius: '10px', cursor: 'pointer',
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper', color: 'text.primary',
                        font: 'inherit',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                      }}
                    >
                      <Icon fontSize='small' sx={{ mt: '2px', color: 'primary.main' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='body2' fontWeight='600'>{it.label}</Typography>
                        <Typography variant='caption' color='text.secondary' display='block'>
                          {it.hint}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
